import { useState, useEffect, useCallback, useRef } from 'react';

/**
 * A useState replacement that persists to sessionStorage.
 * State survives component unmounts (tab switches, route changes)
 * but clears when the browser tab is closed.
 *
 * @param key   Unique sessionStorage key
 * @param initialValue  Fallback when no stored value exists
 */
export function useSessionState<T>(
    key: string,
    initialValue: T | (() => T),
): [T, (value: T | ((prev: T) => T)) => void] {
    const [state, setStateInternal] = useState<T>(() => {
        try {
            const stored = sessionStorage.getItem(key);
            if (stored !== null) return JSON.parse(stored);
        } catch { /* corrupted — fall through */ }
        return typeof initialValue === 'function'
            ? (initialValue as () => T)()
            : initialValue;
    });

    const stateRef = useRef(state);

    const setState = useCallback(
        (value: T | ((prev: T) => T)) => {
            setStateInternal((prev) => {
                const next = typeof value === 'function'
                    ? (value as (prev: T) => T)(prev)
                    : value;
                stateRef.current = next;
                try { sessionStorage.setItem(key, JSON.stringify(next)); } catch { /* quota */ }
                return next;
            });
        },
        [key],
    );

    // Sync to sessionStorage when the component unmounts
    useEffect(() => {
        return () => {
            try { sessionStorage.setItem(key, JSON.stringify(stateRef.current)); } catch { /* quota */ }
        };
    }, [key]);

    return [state, setState];
}

/**
 * Clears all session state keys that start with a given prefix.
 * Useful when navigating away from a module entirely.
 */
export function clearSessionState(prefix: string) {
    const keysToRemove: string[] = [];
    for (let i = 0; i < sessionStorage.length; i++) {
        const k = sessionStorage.key(i);
        if (k?.startsWith(prefix)) keysToRemove.push(k);
    }
    keysToRemove.forEach((k) => sessionStorage.removeItem(k));
}
